import { useEffect, useMemo, useState } from "react";

import {
  GoogleFileSearchStoreModalProps,
  GoogleFileSearchStore,
  GoogleFileSearchDocument,
} from "./types";
import { useStoreCreation } from "./hooks/useStoreCreation";
import { useFileUpload } from "./hooks/useFileUpload";
import { useDocumentManagement } from "./hooks/useDocumentManagement";

import { FileStoreModal, useStoreSelection } from "@/components/FileStoreModal";
import { NormalizedFile, NormalizedStore } from "@/components/FileStoreModal/types";
import {
  useGoogleFileSearchStores,
  useGoogleFileSearchStoreDocuments,
  useDeleteGoogleFileSearchStore,
} from "@/queries/google-file-search-store";
import { displayErrorToast } from "@/utils/toast";

const formatFileSize = (bytes: number | null): string => {
  if (bytes === null || bytes === undefined) {
    return "";
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const GoogleFileSearchStoreModal = ({
  open,
  onClose,
  onSelect,
  initialSelectedNames = [],
  provider = "google",
}: GoogleFileSearchStoreModalProps) => {
  const {
    activeStoreId,
    setActiveStoreId,
    selectedStoreIds,
    toggleStoreSelection,
    isStoreSelected,
  } = useStoreSelection({ initialSelectedIds: initialSelectedNames });

  const {
    isCreating,
    setIsCreating,
    newStoreName,
    setNewStoreName,
    isCreatingStore,
    handleCreateStore,
    handleCancelCreation,
  } = useStoreCreation({
    provider,
    onStoreCreated: storeName => {
      setActiveStoreId(storeName);
      toggleStoreSelection(storeName);
    },
  });

  const {
    selectedFiles,
    isDragOver,
    uploadingFiles,
    handleFileSelect,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleRemoveFile,
    handleUploadAndAttach,
    isUploading,
  } = useFileUpload({ selectedStoreName: activeStoreId, provider });

  const { handleDeleteDocument, isDeletingDocByName } = useDocumentManagement({
    selectedStoreName: activeStoreId,
    provider,
  });

  const deleteStoreMutation = useDeleteGoogleFileSearchStore(provider);
  const [deletingStoreNames, setDeletingStoreNames] = useState<Set<string>>(new Set());

  const handleDeleteStore = (storeName: string) => {
    if (
      !window.confirm("Are you sure you want to delete this store? This action cannot be undone.")
    ) {
      return;
    }
    setDeletingStoreNames(prev => new Set(prev).add(storeName));
    deleteStoreMutation.mutate(
      { name: storeName },
      {
        onSettled: () => {
          setDeletingStoreNames(prev => {
            const next = new Set(prev);
            next.delete(storeName);
            return next;
          });
        },
        onSuccess: () => {
          if (activeStoreId === storeName) {
            setActiveStoreId(null);
          }
        },
      },
    );
  };

  // Data fetching
  const {
    data: storesData,
    isLoading: isLoadingStores,
    error: storesError,
  } = useGoogleFileSearchStores(provider, open);

  const {
    data: documentsData,
    isLoading: isLoadingDocuments,
    error: documentsError,
  } = useGoogleFileSearchStoreDocuments(activeStoreId || undefined, provider);

  useEffect(() => {
    if (storesError) {
      const message =
        storesError instanceof Error
          ? storesError.message
          : "No file search stores found for this workspace.";
      displayErrorToast(message);
    }
  }, [storesError]);

  useEffect(() => {
    if (documentsError) {
      const message =
        documentsError instanceof Error
          ? documentsError.message
          : "No documents found in this store.";
      displayErrorToast(message);
    }
  }, [documentsError]);

  const stores: GoogleFileSearchStore[] = useMemo(() => storesData?.data || [], [storesData?.data]);
  const documents: GoogleFileSearchDocument[] = useMemo(
    () => documentsData?.data || [],
    [documentsData?.data],
  );

  // Normalize stores
  const normalizedStores: NormalizedStore[] = useMemo(
    () =>
      stores.map(store => ({
        id: store.name,
        displayName: store.display_name || store.name,
        subtitle: store.name,
      })),
    [stores],
  );

  // Normalize files
  const normalizedFiles: NormalizedFile[] = useMemo(
    () =>
      documents.map(doc => ({
        id: doc.name,
        displayName: doc.display_name || doc.name,
        sizeText: formatFileSize(doc.size_bytes),
        extraInfo: doc.mime_type || undefined,
        previewId: doc.file_id || undefined,
        previewName: doc.display_name || undefined,
      })),
    [documents],
  );

  const handleSave = () => {
    if (onSelect) {
      onSelect(selectedStoreIds);
    }
    onClose();
  };

  const selectedStore = stores.find(s => s.name === activeStoreId);

  const renderStoreInfo = (storeId: string) => {
    const store = stores.find(s => s.name === storeId);
    if (!store) {
      return null;
    }
    return (
      <>
        <h3 className="text-lg font-semibold">{store.display_name || store.name}</h3>
        <p className="truncate text-sm text-gray-500">{store.name}</p>
        {store.create_time && <p className="text-xs text-gray-400">Created: {store.create_time}</p>}
      </>
    );
  };

  return (
    <FileStoreModal
      open={open}
      onClose={onClose}
      onSave={handleSave}
      saveDisabled={selectedStoreIds.length === 0}
      title="Manage File Search Stores"
      storeListTitle="File Search Stores"
      emptyStoresMessage="No file search stores found"
      emptyFilesMessage="No documents in this store"
      selectStoreMessage="Select a store to view details"
      filesHeaderLabel={count => `Documents (${selectedStore ? documents.length : count})`}
      isCreating={isCreating}
      setIsCreating={setIsCreating}
      newStoreName={newStoreName}
      setNewStoreName={setNewStoreName}
      isCreatingStore={isCreatingStore}
      handleCreateStore={handleCreateStore}
      handleCancelCreation={handleCancelCreation}
      stores={normalizedStores}
      isLoadingStores={isLoadingStores}
      activeStoreId={activeStoreId}
      setActiveStoreId={setActiveStoreId}
      isStoreSelected={isStoreSelected}
      toggleStoreSelection={toggleStoreSelection}
      onDeleteStore={handleDeleteStore}
      isDeletingStore={storeName => deletingStoreNames.has(storeName)}
      files={normalizedFiles}
      isLoadingFiles={isLoadingDocuments}
      uploadingFiles={uploadingFiles}
      selectedFiles={selectedFiles}
      isDragOver={isDragOver}
      isUploading={isUploading}
      handleFileSelect={handleFileSelect}
      handleDragOver={handleDragOver}
      handleDragLeave={handleDragLeave}
      handleDrop={handleDrop}
      handleRemoveFile={handleRemoveFile}
      handleUploadAndAttach={handleUploadAndAttach}
      uploadHelperText="Upload files to this file search store"
      onDeleteFile={file =>
        handleDeleteDocument({
          documentName: file.id,
          fileName: documents.find(d => d.name === file.id)?.file_name ?? "",
          fileId: documents.find(d => d.name === file.id)?.file_id ?? "",
          filename: documents.find(d => d.name === file.id)?.display_name ?? "",
        })
      }
      isFileDeleting={isDeletingDocByName}
      renderStoreInfo={renderStoreInfo}
    />
  );
};
